import { Button, ButtonTheme } from 'components/Button';
import { HeaderToolbar } from 'components/HeaderToolbar';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { Menu, MenuOption } from 'components/Menu';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { SidebarShowButton } from 'componentsAware/SidebarShowButton';
import { useScreenBreakpoints } from 'hooks/useScreenBreakpoints';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  doEngageFlatsLoad,
  doEngageFlatsSetFilter,
} from 'store/remote/engageActions';
import {
  selectEngageFlatsFilterSearch,
  selectEngageFlatsFilterStatuses,
  selectEngageFlatsFilterOccupationStatuses,
} from 'store/remote/engageSelectors';
import {
  EngageFlatsFilter,
  EngageFlatsFilterStatus,
  EngageFlatsStatusFilters,
  EngagePropertyListItem,
  EngageFlatsOccupationStatusFilters,
  FlatOccupationStatus,
  OccupationStatusIcons,
} from 'types/Engage';
type Props = {
  isLoading: boolean;
  property: EngagePropertyListItem;
  onBackPress: () => void;
  onReloadPress: () => void;
  onOnboardFlatsPress: () => void;
  flatsCount: number;
  pageKey: string;
};

const surveyStatusIconColor: Record<EngageFlatsFilterStatus, string> = {
  complete: theme.colors.green,
  incomplete: theme.colors.red,
};

const allOccupationStatusIds = EngageFlatsOccupationStatusFilters.map(
  (status) => status.id,
);

const screenBreakpoints = [
  {
    width: 520,
    key: 1,
  },
  {
    width: 720,
    key: 2,
  },
];

export const EngageFlatsHeader: React.FC<Props> = ({
  isLoading,
  property,
  onBackPress,
  onReloadPress,
  onOnboardFlatsPress,
  flatsCount,
  pageKey,
}) => {
  const dispatch = useDispatch();
  const screenBreakpoint = useScreenBreakpoints(screenBreakpoints) || 0;

  const searchValue = useSelector((state) =>
    selectEngageFlatsFilterSearch(state, pageKey),
  );
  const selectedSurveyStatuses = useSelector((state) =>
    selectEngageFlatsFilterStatuses(state, pageKey),
  );
  const selectedOccupationStatuses = useSelector((state) =>
    selectEngageFlatsFilterOccupationStatuses(state, pageKey),
  );

  const searchValueRef = React.useRef(searchValue);
  const textfieldKeyRef = React.useRef(randomKey());
  if (searchValueRef.current !== searchValue) {
    textfieldKeyRef.current = randomKey();
    searchValueRef.current = searchValue;
  }

  const handleEngageFilter = React.useCallback(
    (filterChanges: Partial<EngageFlatsFilter>) => {
      dispatch(
        doEngageFlatsSetFilter({
          key: pageKey,
          data: filterChanges,
        }),
      );
      dispatch(
        doEngageFlatsLoad({
          key: pageKey,
          propertyId: property.id,
          page: 'reset',
        }),
      );
    },
    [dispatch, pageKey, property.id],
  );

  const handleSearch = React.useCallback(
    (newSearchValue: string) => {
      searchValueRef.current = newSearchValue;
      handleEngageFilter({ search: newSearchValue });
    },
    [handleEngageFilter],
  );

  const handleSelectAllOccupationStatuses = React.useCallback(() => {
    handleEngageFilter({
      occupationStatuses: [...allOccupationStatusIds],
    });
  }, [handleEngageFilter]);

  const handleToggleOccupationStatus = React.useCallback(
    (status: FlatOccupationStatus) => {
      const isSelected = selectedOccupationStatuses.includes(status);
      const nextSelection = isSelected
        ? selectedOccupationStatuses.filter((id) => id !== status)
        : [...selectedOccupationStatuses, status];
      const orderedSelection = allOccupationStatusIds.filter((id) =>
        nextSelection.includes(id),
      );
      handleEngageFilter({ occupationStatuses: orderedSelection });
    },
    [handleEngageFilter, selectedOccupationStatuses],
  );

  const hasSurveyFilter =
    selectedSurveyStatuses.length > 0 &&
    selectedSurveyStatuses.length !== EngageFlatsStatusFilters.length;
  const hasAllOccupationStatusesSelected =
    selectedOccupationStatuses.length === allOccupationStatusIds.length;

  const surveyStatusLabel = React.useMemo(() => {
    if (!hasSurveyFilter) {
      return 'All Survey Statuses';
    }
    const selectedNames = EngageFlatsStatusFilters.filter((status) =>
      selectedSurveyStatuses.includes(status.id),
    ).map((status) => status.name);
    if (!selectedNames.length) {
      return 'All Survey Statuses';
    }
    return selectedNames.length > 1
      ? `${selectedNames[0]}${pluralise(selectedNames.length - 1, '+1', '+{x}', '')}`
      : selectedNames[0];
  }, [hasSurveyFilter, selectedSurveyStatuses]);

  const occupationStatusLabel = React.useMemo(() => {
    if (hasAllOccupationStatusesSelected) {
      return 'All Occ Statuses';
    }
    const selectedNames = EngageFlatsOccupationStatusFilters.filter((status) =>
      selectedOccupationStatuses.includes(status.id),
    ).map((status) => status.name);
    if (!selectedNames.length) {
      return 'All Occupation Statuses';
    }
    return selectedNames.length > 1
      ? `${selectedNames[0]}${pluralise(selectedNames.length - 1, '+1', '+{x}', '')}`
      : selectedNames[0];
  }, [hasAllOccupationStatusesSelected, selectedOccupationStatuses]);

  const surveyStatusMenuOptions = React.useMemo<MenuOption[]>(
    () => [
      ...EngageFlatsStatusFilters.map<MenuOption>((status) => ({
        text: status.name,
        icon: ICONS.FileDuotone,
        iconColor: surveyStatusIconColor[status.id],
        onPress: () => handleEngageFilter({ statuses: [status.id] }),
      })),
      '-',
      {
        text: 'All Survey Statuses',
        onPress: () => handleEngageFilter({ statuses: [] }),
      },
    ],
    [handleEngageFilter],
  );

  const occupationStatusMenuOptions = React.useMemo<MenuOption[]>(
    () => [
      ...EngageFlatsOccupationStatusFilters.map<MenuOption>((status) => ({
        text: status.name,
        leftIcon: selectedOccupationStatuses.includes(status.id)
          ? ICONS.CheckLight
          : ICONS.Blank,
        icon: OccupationStatusIcons[status.id],
        keepMenuOpenOnPress: true,
        onPress: () => handleToggleOccupationStatus(status.id),
      })),
      '-',
      {
        text: 'All Occupation Statuses',
        keepMenuOpenOnPress: true,
        onPress: handleSelectAllOccupationStatuses,
      },
    ],
    [
      handleSelectAllOccupationStatuses,
      handleToggleOccupationStatus,
      selectedOccupationStatuses,
    ],
  );

  const handleOpenFiltersModal = React.useCallback(() => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageFlatsFilter,
        size: 'small',
        params: {
          pageKey,
          propertyId: property.id,
        },
      }),
    );
  }, [dispatch, pageKey, property.id]);

  return (
    <View>
      <NavigationHeader marginRight={0} marginLeft={0}>
        <SidebarShowButton />
        <ViewWithSeparators
          horizontal
          style={{
            alignItems: 'center',
            flex: 1,
          }}
          separatorMargin={theme.paddingHorizontalHalf}
          separatorColor="transparent"
        >
          <Button
            icon={ICONS.ChevronLeftLight}
            text="Engage"
            buttonTheme={ButtonTheme.header}
            onPress={onBackPress}
            style={{
              paddingLeft: theme.paddingHorizontalHalf,
              paddingRight: theme.paddingHorizontal,
            }}
            chipLike
          />
          <Txt weight="bold" numberOfLines={1} style={{ flex: 1 }}>
            {property.name}{' '}
            <Txt
              numberOfLines={1}
              style={{ color: theme.colors.fieldValueText }}
            >
              {isLoading
                ? 'Loading Flats'
                : pluralise(flatsCount, '{x} Flat', '{x} Flats', 'No Flats')}
            </Txt>
          </Txt>
          <Menu
            iconButtonConfig={{ icon: ICONS.EllipsisLight }}
            options={[
              {
                text: 'Export List as CSV',
                icon: ICONS.ArrowDownLight,
              },
              '-',
              {
                text: 'Onboard Flats',
                subText: 'Import new flats into engage',
                onPress: onOnboardFlatsPress,
              },
              '-',
              {
                text: 'Reload',
                icon: ICONS.RotateRightLight,
                onPress: onReloadPress,
              },
            ]}
          />
        </ViewWithSeparators>
      </NavigationHeader>
      <HeaderToolbar
        key={textfieldKeyRef.current}
        searchValue={searchValue}
        onSearch={handleSearch}
        searchPlaceholder="Search flats and contacts"
        searchDisabled={false}
      >
        {screenBreakpoint > 0 && (
          <Menu
            buttonConfig={{
              text: surveyStatusLabel,
              iconRight: ICONS.CaretDownSolid,
              iconRightSize: 12,
              buttonTheme: ButtonTheme.themeless,
              textSize: 'xsmall',
              chipLike: true,
              style: {
                maxWidth: 180,
              },
            }}
            width={220}
            options={surveyStatusMenuOptions}
          />
        )}
        {screenBreakpoint > 1 && (
          <Menu
            buttonConfig={{
              text: occupationStatusLabel,
              iconRight: ICONS.CaretDownSolid,
              iconRightSize: 12,
              buttonTheme: ButtonTheme.themeless,
              textSize: 'xsmall',
              chipLike: true,
              style: {
                maxWidth: 220,
              },
            }}
            width={240}
            options={occupationStatusMenuOptions}
          />
        )}
        <IconButton
          icon={ICONS.SlidersUpLight}
          onPress={handleOpenFiltersModal}
          showBadge={hasSurveyFilter || !hasAllOccupationStatusesSelected}
        />
      </HeaderToolbar>
    </View>
  );
};
